import * as React                                                from 'react';
import { useSelector, 
         useDispatch 
       }                                                         from 'react-redux';
import { useEffect, 
         useMemo, 
         useState 
       }                                                         from 'react';
import { Box, Card, 
         CardContent, 
         Typography,
         useTheme
       }                                                         from '@mui/material';
import { DataGridPro, 
         GridColDef, 
         GridRowId, 
         useGridApiRef 
       }                                                         from '@mui/x-data-grid-pro';
import { useGlobalConfig }                                       from 'context/GlobalContext';
import { selectCMSSelectedPilotRecoScore, 
         selectCMSSelectedPilotDocId, 
         setSelectedPilotRecoScore 
       }                                                         from 'store/dataReducers/cmsPilotSlice';
import { selectDraftRecos }                                      from 'store/dataReducers/recommendationsSlice';
import { selectCategories }                                      from 'store/dataReducers/applianceProfileSlice';
import { CMSPilotApis }                                             from 'apis/cmsPilotAPI';
import { BglCloseOutlinedSmallButton,
         BglPrimaryOutlinedSmallButton 
       }                                                         from 'components/button/BglButtons';
import { BglAlertDialog }                                        from 'components/dialog/BglAlertDialog';
import SaveIcon                                                  from '@mui/icons-material/Save';
import RestoreIcon                                               from '@mui/icons-material/Restore';
import { getRecoScorePageStyles }                                from './RecoScorePageStyles';

export interface RecoScoreThreshold {
  max: number;
  min: number;
}

interface RecoScoreData {
  categoryId: string;
  categoryName: string;
  max: number;
  min: number;
  numberOfRecos: number;
  step: number;
}

export const RecoScorePage = () => {

  const { rc }      = useGlobalConfig();
  const theme       = useTheme();
  const styles      = getRecoScorePageStyles(theme);
  const dispatch    = useDispatch();
  const apiRef      = useGridApiRef();
  
  const selectedPilotRecoScore  = useSelector(selectCMSSelectedPilotRecoScore);
  const selectedPilotDocId      = useSelector(selectCMSSelectedPilotDocId);
  const draftRecos              = useSelector(selectDraftRecos);
  const categories              = useSelector(selectCategories);

  const canRecoScoreUpdate = rc.permissionsManager.canRecoScoreUpdate();

  const [unsavedRows, setUnsavedRows] =           useState<Record<string, Partial<RecoScoreData>>>({});
  const [isSaving, setIsSaving]                   = useState(false);
  
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    severity: 'info' | 'warning' | 'error' | 'success';
    onConfirm?: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    severity: 'info'
  });

  // Fetch reco score if not available
  useEffect(() => {
    const fetchRecoScore = async () => {
      if (!selectedPilotRecoScore) {
        try {
          const response = await CMSPilotApis.getRecoScore(rc, selectedPilotDocId);
          if (response.data) {
            dispatch(setSelectedPilotRecoScore(response.data)); 
          }
        } catch (error) {
          console.error('Error fetching reco score:', error);
        }
      }
    };

    fetchRecoScore();
  });

  // Process data for the table with real-time step calculation
  const getTableData : RecoScoreData[] = useMemo(() => {

    if (!selectedPilotRecoScore) {
      return [];
    }

    const thresholds = selectedPilotRecoScore.thresholds as unknown as Record<string, RecoScoreThreshold>;
    
    return Object.keys(thresholds).map(categoryId => {

      const threshold = thresholds[categoryId];
      const categoryRecos = draftRecos.filter(reco => 
        (reco as any).category === categoryId
      );
      const numberOfRecos = categoryRecos.length;
      
      // Get current values (including unsaved changes)
      const unsavedRow = unsavedRows[categoryId];
      const currentMax = unsavedRow?.max ?? threshold?.max ?? 0;
      const currentMin = unsavedRow?.min ?? threshold?.min ?? 0;
      
      const step = numberOfRecos > 0 
        ? (currentMax - currentMin) / numberOfRecos 
        : 0;

      return {
        categoryId: categoryId,
        categoryName: categories.find(category => category.categoryId === categoryId)?.name || '',
        max: currentMax,
        min: currentMin,
        numberOfRecos,
        step: Math.round(step * 10000) / 10000 // Round to 2 decimal places
      };
    });
  }, [selectedPilotRecoScore, unsavedRows, draftRecos]);

  // Process row updates
  const processRowUpdate = React.useCallback((newRow: RecoScoreData, oldRow: RecoScoreData) => {
    const hasChanges = newRow.max !== oldRow.max || newRow.min !== oldRow.min;
    
    if (hasChanges) {
      setUnsavedRows(prev => ({
        ...prev,
        [newRow.categoryId]: {
          max: newRow.max,
          min: newRow.min
        }
      }));
    } else {
      setUnsavedRows(prev => {
        const newUnsavedRows = { ...prev };
        delete newUnsavedRows[newRow.categoryId];
        return newUnsavedRows;
      });
    }

    // Return the updated row with recalculated step
    const updatedRow = { ...newRow };
    const categoryRecos = draftRecos.filter(reco => 
      (reco as any).category === newRow.categoryId
    );
    const numberOfRecos = categoryRecos.length;
    const step = numberOfRecos > 0 
      ? (newRow.max - newRow.min) / numberOfRecos 
      : 0;
    
    updatedRow.step = Math.round(step * 10000) / 10000;
    
    return updatedRow;
  }, [draftRecos]);

  // Update table rows when unsavedRows changes
  useEffect(() => {
    if (getTableData.length > 0) {
      apiRef.current?.updateRows(getTableData);
    }
  }, [getTableData, apiRef]);

  const onProcessRowUpdateError = React.useCallback((error: any) => {
    console.error('Error updating row:', error);
  }, []);

  // Validate row values
  const validateRow = (row: RecoScoreData): boolean => {
    const unsavedRow = unsavedRows[row.categoryId];
    if (!unsavedRow) return true;

    const max = unsavedRow.max ?? row.max;
    const min = unsavedRow.min ?? row.min;

    return max >= 0 && max <= 1 && min >= 0 && min <= 1 && min < max;
  };

  // Validate all rows
  const validateAllRows = (): boolean => {
    return getTableData.every(row => validateRow(row));
  };

  // Save changes
  const saveChanges = async () => {
    if (!validateAllRows()) {
      setAlertDialog({
        open: true,
        title: 'Validation Error',
        message: 'Please fix validation errors before saving. Values must be between 0 and 1, and min must be less than max.',
        severity: 'error'
      });
      return;
    }

    if (!selectedPilotRecoScore?.documentId) {
      setAlertDialog({
        open: true,
        title: 'No Data Available',
        message: 'No reco score data available.',
        severity: 'error'
      });
      return;
    }

    setIsSaving(true);

    try {
      // Prepare thresholds object with only changed values
      const updatedThresholds: Record<string, RecoScoreThreshold> = {};
      
      getTableData.forEach(row => {
        const unsavedRow = unsavedRows[row.categoryId];
        
        // Only include thresholds that have actually changed
        if (unsavedRow && selectedPilotRecoScore.thresholds) {
          const thresholds = selectedPilotRecoScore.thresholds as unknown as Record<string, RecoScoreThreshold>;
          const originalThreshold = thresholds[row.categoryId];
          const hasMaxChanged = unsavedRow.max !== undefined && unsavedRow.max !== originalThreshold?.max;
          const hasMinChanged = unsavedRow.min !== undefined && unsavedRow.min !== originalThreshold?.min;
          
          if (hasMaxChanged || hasMinChanged) {
            updatedThresholds[row.categoryId] = {
              max: unsavedRow.max ?? originalThreshold?.max ?? 0,
              min: unsavedRow.min ?? originalThreshold?.min ?? 0
            };
          }
        }
      });

      // Check if there are any changes to send
      if (Object.keys(updatedThresholds).length === 0) {
        return;
      }

      const response = await CMSPilotApis.updateRecoScore(
        rc, 
        selectedPilotRecoScore.documentId, 
        updatedThresholds
      );

      if (response.data) {
        dispatch(setSelectedPilotRecoScore(response.data));
        setUnsavedRows({});
      }
    } catch (error) {
      console.error('Error saving reco score:', error);
      setAlertDialog({
        open: true,
        title: 'Save Error',
        message: 'Error saving changes. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Discard changes
  const discardChanges = () => {
    setUnsavedRows({});
    // Reset table to original values
    apiRef.current?.updateRows(getTableData);
  };

  // Show discard confirmation dialog
  const handleDiscardClick = () => {
    setAlertDialog({
      open: true,
      title: 'Discard Changes',
      message: 'Are you sure you want to discard all unsaved changes? This action cannot be undone.',
      severity: 'warning',
      onConfirm: discardChanges
    });
  };

  // Get row class name for highlighting edited rows
  const getRowClassName = React.useCallback(({ id }: { id: GridRowId }) => {
    const row = getTableData.find(r => r.categoryId === id);
    if (!row) return '';
    
    const isEdited = unsavedRows[id];
    const isValid = validateRow(row);
    
    if (isEdited && !isValid) return 'row--edited row--invalid';
    if (isEdited) return 'row--edited';
    return '';
  }, [unsavedRows, getTableData]);

  // Check if cell is editable
  const isCellEditable = React.useCallback((params: any) => {
    return params.field === 'max' || params.field === 'min';
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'categoryName',
      headerName: 'Category Name',
      width: 200,
      flex: 1,
      editable: false,
    },
    {
      field: 'max',
      headerName: 'Max',
      width: 150,
      type: 'number',
      editable: canRecoScoreUpdate,
      valueParser: (value: any) => {
        const num = Number(value);
        return isNaN(num) ? 0 : Math.max(0, Math.min(1, num));
      },
    },
    {
      field: 'min',
      headerName: 'Min',
      width: 150,
      type: 'number',
      editable: canRecoScoreUpdate,
      valueParser: (value: any) => {
        const num = Number(value);
        return isNaN(num) ? 0 : Math.max(0, Math.min(1, num));
      },
    },
    {
      field: 'numberOfRecos',
      headerName: 'Number of Recos',
      width: 150,
      type: 'number',
      editable: false,
    },
    {
      field: 'step',
      headerName: 'Step',
      width: 150,
      type: 'number',
      editable: false,
    },
  ];

  const hasUnsavedRows = Object.keys(unsavedRows).length > 0;

  return (
    <Box sx={styles.recoScoreContainer}>

      {/* Guidelines */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1" sx={{ 
              fontWeight: 'bold',
              color: theme.palette.text.secondary
            }}>Guidelines</Typography>
            <Typography variant="body2" sx={{ 
              color: theme.palette.text.secondary
            }}>Update the <b>Max</b> and <b>Min</b> values in the table indicating what should be the maximum and minimum scores for the recos in that category. 
            <br/>The ideal outcome is to have a 'gradient' of scores across all recos for the same category. This should help to prevent having too many recos of the same category in the same notification.</Typography>
          </Box>
        </CardContent>
      </Card> 

      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <BglCloseOutlinedSmallButton
          disabled={!hasUnsavedRows || isSaving}
          onClick={handleDiscardClick}
          startIcon={<RestoreIcon />}>
          {"Discard all" + (Object.keys(unsavedRows).length > 0 ? ` (${Object.keys(unsavedRows).length})` : '')}
        </BglCloseOutlinedSmallButton>

        <BglPrimaryOutlinedSmallButton
          disabled={!hasUnsavedRows || isSaving}
          loading={isSaving}
          onClick={saveChanges}
          startIcon={<SaveIcon />}
          loadingPosition="start">
          {"Save all" + (Object.keys(unsavedRows).length > 0 ? ` (${Object.keys(unsavedRows).length})` : '')}
        </BglPrimaryOutlinedSmallButton>
      </Box>
            
      <DataGridPro
        apiRef={apiRef}
        rows={getTableData}
        getRowId={(row) => row.categoryId}
        columns={columns}
        pagination
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
        sx={styles.dataGrid}
        disableRowSelectionOnClick
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        getRowClassName={getRowClassName}
        isCellEditable={isCellEditable}
      />

      <br/>

      {/* Generic Alert Dialog */}
      <BglAlertDialog
        open={alertDialog.open}
        title={alertDialog.title}
        message={alertDialog.message}
        onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
        onConfirm={alertDialog.onConfirm}
        confirmText={alertDialog.severity === 'warning' ? 'Discard' : 'OK'}
        cancelText="Cancel"
        severity={alertDialog.severity}
        showCancel={alertDialog.severity === 'warning'}
      />

    </Box>
  );
};